import * as React from 'react';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {SelectedFieldCard} from 'shells/form/sidebar-fields/selected-field-card';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import type {Option} from 'shells/select-picker-generic/types';
import {SelectTwoLineIcon} from 'shells/select-two-line-icon/select-two-line-icon';
import type {TwoLineOptionWithIcon} from 'shells/select-two-line-icon/select-two-line-icon';
import {Body} from 'shells/typography/body';

import type {EntityType} from 'nutshell-graphql-types';

import {GraphQLSelectProducts} from '../../../../../core/select/graphql-select-products';
import {GraphQLSelectSources} from '../../../../../core/select/graphql-select-sources';

import {nextStepTypeValues, nextStepHumanReadableTypes} from './consts';
import type {NextStepTypeEnum} from './consts';
import {SelectAudience} from './select-audience';
import {SelectTag} from './select-tag';

type Props = {
    entityType: EntityType;
    handleSubmit: (assignmentId: string, assignmentName: string) => undefined | Promise<any>;
    onCancel: () => void;
    isSaving: boolean;
};

export function AnswerRuleNextStepSelect(props: Props) {
    const [selectedNextStep, setSelectedNextStep] = React.useState<
        TwoLineOptionWithIcon | null | undefined
    >(null);
    const [selectedOption, setSelectedOption] = React.useState<Option | null | undefined>(null);

    const selectComponent = selectedNextStep
        ? getSelectComponent(
              props.entityType,
              // @ts-expect-error
              selectedNextStep.value,
              setSelectedOption,
              selectedOption && selectedOption.value
          )
        : undefined;

    return (
        <StackLegacy spacing={8} isFullHeight={true}>
            <Body>Then...</Body>
            <SelectTwoLineIcon
                options={nextStepTypeValues[props.entityType]}
                placeholder='Select a next step'
                // @ts-expect-error
                onChange={(nextStep) => {
                    setSelectedNextStep(nextStep);
                    setSelectedOption(null);
                }}
                value={selectedNextStep}
            />
            {selectedNextStep && (
                // @ts-expect-error
                <Body>Which {nextStepHumanReadableTypes[selectedNextStep.value]}?</Body>
            )}
            {!selectedOption && selectComponent}
            {selectedNextStep && selectedOption && (
                <SelectedFieldCard
                    name={selectedOption.label}
                    onRemove={() => setSelectedOption(null)}
                />
            )}
            <div className='flex justify-end gap-8'>
                <Button variant='secondary' onClick={props.onCancel}>
                    Cancel
                </Button>
                <SaveButton
                    isSaving={props.isSaving}
                    // @ts-expect-error
                    onClick={() => {
                        if (selectedOption && selectedNextStep) {
                            props.handleSubmit(selectedOption.value, selectedOption.label);
                        }
                    }}
                    buttonText='Apply'
                    disabled={!selectedNextStep || !selectedOption}
                />
            </div>
        </StackLegacy>
    );
}

function getSelectComponent(
    entityType: EntityType,
    selectType: NextStepTypeEnum,
    onSelect: (option: Option) => void,
    selectedId?: string | null
) {
    switch (selectType) {
        case 'ADD_AUDIENCES':
            return <SelectAudience onChange={onSelect} selectedId={selectedId} />;
        case 'ADD_SOURCES':
            return <GraphQLSelectSources onChange={onSelect} selectedId={selectedId} />;
        case 'ADD_PRODUCTS':
            return <GraphQLSelectProducts onChange={onSelect} selectedId={selectedId} />;
        case 'ADD_TAGS':
        default:
            return <SelectTag tagType={entityType} onChange={onSelect} selectedId={selectedId} />;
    }
}
